import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from './onboarding-api'
import { InformationalWorkingHoursSettings } from '../settings/InformationalWorkingHoursSettings'
import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'

export function PmsOptionalPage({
  client = getOnboardingApiClient(),
  tenantUid,
  hoursOnly = false,
}: {
  client?: Pick<OnboardingApiClient, 'pmsCapability'>
  tenantUid?: string
  hoursOnly?: boolean
}) {
  const auth = useAuth()
  const [params] = useSearchParams()
  const user = auth.status === 'authenticated' ? auth.user : null
  const tenant = tenantUid ?? params.get('tenantUid') ?? user?.uid ?? ''
  const queries = useQueryClient()
  const key = ['pms-capability', user?.uid, tenant]
  const query = useQuery({
    queryKey: key,
    enabled: !!user,
    queryFn: () => client.pmsCapability!(user!, tenant),
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  async function save(command: Record<string, unknown>) {
    if (!user || !query.data) return
    setBusy(true)
    setError(false)
    try {
      const value = await client.pmsCapability!(user, tenant, {
        ...command,
        expectedRevision: query.data.revision,
      })
      queries.setQueryData(key, value)
      await queries.invalidateQueries({ queryKey: ['onboarding'] })
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }
  if (query.isPending) return <Spinner label="Loading clinic setup" />
  if (query.isError || !query.data)
    return (
      <p role="alert">
        Clinic setup is unavailable. Check your clinic access and try again.
      </p>
    )
  const data = query.data
  if (hoursOnly)
    return (
      <InformationalWorkingHoursSettings tenantUid={tenant} client={client} />
    )
  return (
    <Card>
      <h1>Choose your clinic’s PMS setup</h1>
      <p>
        Connect a PMS for patient and appointment actions, or continue with
        informational AI Voice and Flio-owned clinic hours.
      </p>
      {error && (
        <p role="alert">
          The change could not be saved. Refresh to review the current clinic
          state before retrying.
        </p>
      )}
      {data.pmsStatus === 'PMS_REQUIRED_BUT_INCOMPLETE' && (
        <>
          {tenant === user?.uid && (
            <Link to="/onboarding/nexhealth">Connect PMS</Link>
          )}
          <p>
            Without a PMS, appointment availability, patient and appointment
            lookups, booking, cancellation and rescheduling remain unavailable.
            Voice still requires Communications setup and activation.
          </p>
          <Button
            disabled={busy || data.status !== 'CRM_CREDENTIAL_REQUIRED'}
            onClick={() => void save({ action: 'SELECT_NO_PMS' })}
          >
            Continue without PMS
          </Button>
        </>
      )}
      {data.pmsStatus === 'PMS_CONNECTED' && (
        <p>
          This clinic uses its existing PMS integration. Manage it through the
          integration workflow.
        </p>
      )}
      {['PMS_INTENTIONALLY_DISABLED', 'PMS_CONNECTION_IN_PROGRESS'].includes(
        data.pmsStatus
      ) && (
        <>
          <p>
            Select Voice only in Communications setup. PMS is disabled by
            choice. AI must not claim to check appointment availability or
            complete appointment actions.
          </p>
          {data.pmsConnectionStatus === 'NOT_STARTED' ? (
            <>
              <p>
                You can connect a PMS later. This starts integration setup;
                Voice ownership stays in place and appointment actions remain
                disabled until real provider setup and validation complete.
              </p>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void save({ action: 'BEGIN_PMS_CONNECTION' })}
              >
                Begin PMS connection
              </Button>
            </>
          ) : (
            <p>
              PMS connection setup has started. Complete the canonical provider
              credential, institution/location and scheduling review with your
              Organization administrator. Appointment actions remain disabled.
            </p>
          )}
          <InformationalWorkingHoursSettings
            tenantUid={tenant}
            client={client}
          />
          {data.status === 'COMMUNICATIONS_REQUIRED' && (
            <Link
              to={
                tenant === user?.uid
                  ? '/onboarding/communications'
                  : `/organization/clinics/${encodeURIComponent(tenant)}/communications`
              }
            >
              Continue to Communications setup
            </Link>
          )}
          {data.status === 'READY' && (
            <Link to="/settings">Return to Settings</Link>
          )}
        </>
      )}
    </Card>
  )
}
